import { Injectable, computed, inject, signal } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError, map, tap } from 'rxjs/operators';
import { AccessRequest, AccessRequestListResponse, EnvironmentEntry, Sensitivity } from '../models/api.models';
import { environment } from '../../../environments/environment';

const ADMIN_BASE = environment.apiBaseUrl + '/v1/admin';

@Injectable({ providedIn: 'root' })
export class PlatformAdminService {
  private readonly httpClient = inject(HttpClient);

  private readonly _requests = signal<AccessRequest[]>([]);
  readonly requests    = this._requests.asReadonly();
  readonly unreadCount = computed(() => this._requests().filter(r => !r.read_at).length);

  loadRequests(): Observable<AccessRequest[]> {
    return this.httpClient.get<AccessRequestListResponse>(`${ADMIN_BASE}/platform-admin/access-requests`)
      .pipe(
        map(r => r.access_requests ?? []),
        tap(reqs => this._requests.set(reqs)),
      );
  }

  markRead(id: string): Observable<void> {
    this._requests.update(reqs =>
      reqs.map(r => r.id === id ? { ...r, read_at: new Date().toISOString() } : r)
    );
    return this.httpClient.patch<void>(`${ADMIN_BASE}/platform-admin/access-requests/${id}/read`, {}).pipe(
      catchError(err => {
        this._requests.update(reqs =>
          reqs.map(r => r.id === id ? { ...r, read_at: null } : r)
        );
        return throwError(() => err);
      }),
    );
  }

  deleteAccessRequest(id: string): Observable<void> {
    const prev = this._requests();
    this._requests.update(reqs => reqs.filter(r => r.id !== id));
    return this.httpClient.delete<void>(`${ADMIN_BASE}/platform-admin/access-requests/${id}`).pipe(
      catchError(err => {
        this._requests.set(prev);
        return throwError(() => err);
      }),
    );
  }

  private readonly envBase = `${ADMIN_BASE}/platform-admin/platform/environments`;

  listEnvironments(): Observable<EnvironmentEntry[]> {
    return this.httpClient
      .get<{ environments: EnvironmentEntry[] }>(this.envBase)
      .pipe(map(r => r.environments ?? []));
  }

  createEnvironment(e: EnvironmentEntry): Observable<void> {
    return this.httpClient.post<void>(this.envBase, e);
  }

  updateEnvironment(name: string, sensitivity: Sensitivity, displayName: string): Observable<void> {
    return this.httpClient.patch<void>(
      `${this.envBase}/${encodeURIComponent(name)}`,
      { sensitivity, display_name: displayName });
  }

  deleteEnvironment(name: string): Observable<void> {
    return this.httpClient.delete<void>(`${this.envBase}/${encodeURIComponent(name)}`);
  }
}
